import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { ViiluDataGrid, PageHeader, PageContent, ConfirmDialog, useRouter, useModuleAccess } from '@viilutalous/common';
import { PayrollLinkChip, employeeLinksAPI, useEmployeePayroll } from '@viilutalous/salaries';
import { employeesAPI } from './employeesAPI';
import { useTranslation } from 'react-i18next';

export default function Employees({ tenantId }) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isEnabled: payrollEnabled } = useModuleAccess('salaries');
  // Payroll link per employee id; null while unknown (no payroll actions are offered then).
  const [links, setLinks] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (tenantId) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Module access may resolve after the list, so payroll chips load on their own.
  const loadLinks = useCallback(() => {
    if (!payrollEnabled || employees.length === 0) return;
    employeeLinksAPI
      .status(tenantId, employees.map((e) => e.id))
      .then((res) => setLinks(res.data.links || {}))
      .catch(() => setLinks(null));
  }, [payrollEnabled, employees, tenantId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const payroll = useEmployeePayroll(tenantId, {
    // List rows carry no henkilötunnus; the push dialog needs it.
    loadEmployee: async (employeeId) => (await employeesAPI.getOne(tenantId, employeeId)).data,
    onChanged: loadLinks,
  });

  const fetchEmployees = async () => {
    if (!tenantId) return;

    try {
      const response = await employeesAPI.getAll(tenantId);
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    navigate('/employees/new');
  };

  const handleEdit = (employee) => {
    navigate(`/employees/${employee.id}`);
  };

  const handleDelete = async () => {
    const id = deleting;
    setDeleting(null);
    if (!tenantId || !id) return;
    try {
      await employeesAPI.delete(tenantId, id);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
    }
  };

  const getContextMenuItems = (params) => {
    const result = [
      {
        name: t('common.new'),
        action: () => navigate('/employees/new'),
        icon: '<i class="ag-icon ag-icon-plus"/>',
        shortcut: 'Ins',
      },
    ];

    if (!params.node) return result;

    const employee = params.node.data;
    result.push('separator');
    result.push({
      name: t('common.edit'),
      action: () => handleEdit(employee),
      icon: '<i class="ag-icon ag-icon-edit"/>',
      shortcut: 'Enter',
    });

    if (payrollEnabled) {
      const payrollItems = payroll.actionsFor(employee, links ? links[employee.id] || { linked: false } : undefined);
      if (payrollItems.length > 0) {
        result.push('separator');
        payrollItems.forEach((item) => {
          result.push({
            name: item.label,
            action: item.onClick,
            icon: item.icon,
            secondary: true,
            ...(item.color === 'error' ? { cssClasses: ['ag-menu-option-text-danger'] } : {}),
          });
        });
      }
    }

    result.push('separator');
    result.push({
      name: t('common.delete'),
      action: () => setDeleting(employee.id),
      icon: '<i class="ag-icon ag-icon-cancel"/>',
      cssClasses: ['ag-menu-option-text-danger'],
    });

    return result;
  };

  const columnDefs = [
    { field: 'name', headerName: t('employees.name'), flex: 1, minWidth: 180 },
    { field: 'work_email', headerName: t('employees.work_email'), flex: 1, minWidth: 180 },
    { field: 'work_phone', headerName: t('employees.work_phone'), width: 140 },
    { field: 'department_name', headerName: t('employees.department'), width: 160 },
    { field: 'job_title', headerName: t('employees.job_title'), width: 160 },
    ...(payrollEnabled
      ? [{
        field: 'payroll',
        headerName: t('salaries.employees.column'),
        width: 140,
        sortable: false,
        cellRenderer: (p) => <PayrollLinkChip status={links?.[p.data?.id]} />,
      }]
      : []),
  ];

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('employees.select_tenant')}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <ViiluDataGrid
          tenantId={tenantId}
          gridId="employees-list"
          rowData={employees}
          columnDefs={columnDefs}
          loading={loading}
          onRowDoubleClicked={(event) => navigate(`/employees/${event.data.id}`)}
          getContextMenuItems={getContextMenuItems}
          onNew={handleNew}
          onEdit={(row) => navigate(`/employees/${row.id}`)}
          onRefresh={fetchEmployees}
        />
      </PageContent>

      <ConfirmDialog
        open={!!deleting}
        message={t('employees.delete_confirm')}
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
      {payroll.dialogs}
    </>
  );
}
